import type { Broker } from './types';

// Données brokers embarquées (comparateur neutre). Source : dataset de mission
// vérifié le 2026-07-24, complété par la vague 2 (vérifiée le 2026-07-26 :
// 10 brokers supplémentaires, 1 correction d'audit appliquée — spread CMC).
// `logoUrl: null` partout (l'utilisateur complétera).
// Plateformes propriétaires (terminaux/apps maison) normalisées en `'autre'` ;
// les mentions "MT5 (selon régions/entités)" sont ramenées à `'MT5'`.

export const BROKERS: readonly Broker[] = [
  {
    name: 'IC Markets',
    slug: 'ic-markets',
    notorietyRank: 1,
    website: 'https://www.icmarkets.com',
    logoUrl: null,
    foundedYear: 2007,
    headquarters: 'Sydney, Australie',
    regulation: ['ASIC (AU)', 'CySEC (CY)', 'FSA (SC)', 'SCB (BS)', 'CMA (KE)'],
    minDepositUsd: 200,
    spreadFromPips: 0,
    commissionPerLotUsd: 7,
    maxLeverage: "1:500 (int'l) ; 1:30 UE/AU",
    platforms: ['MT4', 'MT5', 'cTrader', 'TradingView'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'obligations', 'crypto (CFD)', 'futures (CFD)'],
    trustpilotScore: 4.8,
    trustpilotReviews: 52542,
    notes: 'Compte Raw Spread : 7 $ AR sur MT4/5, 6 $ AR sur cTrader',
    sources: [
      'https://www.icmarkets.com',
      'https://www.forexbrokers.com/reviews/ic-markets',
      'https://www.trustpilot.com/review/icmarkets.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Exness',
    slug: 'exness',
    notorietyRank: 2,
    website: 'https://www.exness.com',
    logoUrl: null,
    foundedYear: 2008,
    headquarters: 'Limassol, Chypre',
    regulation: ['FSA (SC)', 'CySEC (CY)', 'FCA (UK)', 'FSCA (ZA)', 'CMA (KE)', 'FSC (MU)', 'FSC (BVI)', 'JSC (JO)', 'CBCS (CW)'],
    minDepositUsd: 10,
    spreadFromPips: 0,
    commissionPerLotUsd: 7,
    maxLeverage: '1:2000 (illimité sous conditions)',
    platforms: ['MT4', 'MT5', 'autre'],
    instruments: ['forex', 'métaux', 'indices', 'énergies', 'actions (CFD)', 'crypto (CFD)'],
    trustpilotScore: 4.7,
    trustpilotReviews: 28910,
    notes: 'Raw Spread : 3,50 $/côté ; Standard sans commission dès ~0,2 pip ; retail UE/UK non accepté. Dépôt min 10 $ (Standard), 200 $ (Pro/Raw/Zero). Terminaux maison Exness Terminal / Exness Trade (app) normalisés en « autre ».',
    sources: [
      'https://www.exness.com',
      'https://www.forexbrokers.com/reviews/exness',
      'https://www.trustpilot.com/review/exness.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'XM',
    slug: 'xm',
    notorietyRank: 3,
    website: 'https://www.xm.com',
    logoUrl: null,
    foundedYear: 2009,
    headquarters: 'Limassol, Chypre',
    regulation: ['CySEC (CY)', 'ASIC (AU)', 'FSC (BZ)', 'DFSA (AE)', 'FSCA (ZA)'],
    minDepositUsd: 5,
    spreadFromPips: 0.6,
    commissionPerLotUsd: 0,
    maxLeverage: "1:1000 (int'l) ; 1:30 UE/AU",
    platforms: ['MT4', 'MT5', 'autre', 'TradingView'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'crypto (CFD)'],
    trustpilotScore: 3.8,
    trustpilotReviews: 3080,
    notes: 'Ultra Low dès 0,6 pip sans commission ; Standard dès ~1,6 pip. Score Trustpilot volatil (3,1–3,8 selon la date). App maison XM App normalisée en « autre ».',
    sources: [
      'https://www.xm.com/regulation',
      'https://www.forexbrokers.com/reviews/xm',
      'https://www.trustpilot.com/review/xm.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Pepperstone',
    slug: 'pepperstone',
    notorietyRank: 4,
    website: 'https://pepperstone.com',
    logoUrl: null,
    foundedYear: 2010,
    headquarters: 'Melbourne, Australie',
    regulation: ['ASIC (AU)', 'FCA (UK)', 'CySEC (CY)', 'BaFin (DE)', 'DFSA (AE)', 'CMA (KE)', 'SCB (BS)'],
    minDepositUsd: 0,
    spreadFromPips: 0,
    commissionPerLotUsd: 7,
    maxLeverage: '1:200 retail (SCB) ; 1:30 UE/UK/AU ; 1:500 pro',
    platforms: ['MT4', 'MT5', 'cTrader', 'TradingView', 'autre'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'ETF (CFD)', 'crypto (CFD)'],
    trustpilotScore: 4.3,
    trustpilotReviews: 3516,
    notes: 'Razor : ~3,50 $/côté ; aucun dépôt minimum (200 $ recommandé). App/web maison Pepperstone normalisée en « autre ».',
    sources: [
      'https://pepperstone.com',
      'https://www.forexbrokers.com/reviews/pepperstone',
      'https://www.trustpilot.com/review/pepperstone.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'IG',
    slug: 'ig',
    notorietyRank: 5,
    website: 'https://www.ig.com',
    logoUrl: null,
    foundedYear: 1974,
    headquarters: 'Londres, Royaume-Uni',
    regulation: ['FCA (UK)', 'ASIC (AU)', 'CFTC/NFA (US)', 'MAS (SG)', 'FINMA (CH)', 'JFSA (JP)', 'FMA (NZ)', 'CySEC (CY)', 'FSCA (ZA)', 'DFSA (AE)', 'BMA (BM)'],
    minDepositUsd: 0,
    spreadFromPips: 0.6,
    commissionPerLotUsd: 0,
    maxLeverage: '1:30 retail (1:222 pro)',
    platforms: ['autre', 'MT4', 'TradingView'],
    instruments: ['forex', 'indices', 'actions', 'matières premières', 'ETF', 'options', 'obligations', 'crypto (selon région)'],
    trustpilotScore: 3.8,
    trustpilotReviews: 9000,
    notes: 'Forex spread-only (EUR/USD dès 0,6 pip) ; coté à Londres ; US via tastyfx. Nb d\'avis Trustpilot approximatif (>9000). Plateformes maison IG / ProRealTime / L2 Dealer normalisées en « autre ».',
    sources: [
      'https://www.ig.com',
      'https://www.forexbrokers.com/reviews/ig',
      'https://www.trustpilot.com/review/ig.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'OANDA',
    slug: 'oanda',
    notorietyRank: 6,
    website: 'https://www.oanda.com',
    logoUrl: null,
    foundedYear: 1996,
    headquarters: 'New York, États-Unis',
    regulation: ['CFTC/NFA (US)', 'FCA (UK)', 'ASIC (AU)', 'CIRO (CA)', 'MAS (SG)', 'JFSA (JP)', 'KNF (PL)', 'FSC (BVI)'],
    minDepositUsd: 0,
    spreadFromPips: null,
    commissionPerLotUsd: 0,
    maxLeverage: '1:50 US ; 1:30 UK/UE ; 1:888 (BVI)',
    platforms: ['autre', 'MT4', 'MT5', 'TradingView'],
    instruments: ['forex', 'indices (CFD)', 'matières premières', 'métaux', 'crypto (selon région)'],
    trustpilotScore: 3.9,
    trustpilotReviews: 1150,
    notes: 'Standard spread-only (EUR/USD moyen ~1,7 pip, pas de « from » officiel publié) ; Core pricing + commission en option. Score Trustpilot variable (3,7–4,1). Plateforme maison OANDA Trade normalisée en « autre ».',
    sources: [
      'https://www.oanda.com',
      'https://www.forexbrokers.com/reviews/oanda',
      'https://www.trustpilot.com/review/www.oanda.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Vantage',
    slug: 'vantage',
    notorietyRank: 7,
    website: 'https://www.vantagemarkets.com',
    logoUrl: null,
    foundedYear: 2009,
    headquarters: 'Sydney, Australie',
    regulation: ['ASIC (AU)', 'FCA (UK)', 'FSCA (ZA)', 'CIMA (KY)', 'FSC (MU)', 'VFSC (VU)'],
    minDepositUsd: 50,
    spreadFromPips: 0,
    commissionPerLotUsd: 6,
    maxLeverage: "1:2000 (int'l) ; 1:30 ASIC/FCA",
    platforms: ['MT4', 'MT5', 'TradingView', 'autre'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'ETF (CFD)', 'obligations', 'crypto (CFD)'],
    trustpilotScore: 4.3,
    trustpilotReviews: 13578,
    notes: 'Raw ECN : 3 $/côté ; compte Premium Unlimited (2026) avec levier illimité. App maison Vantage normalisée en « autre ».',
    sources: [
      'https://www.vantagemarkets.com',
      'https://www.forexbrokers.com/reviews/vantage',
      'https://www.trustpilot.com/review/vantagemarkets.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'FP Markets',
    slug: 'fp-markets',
    notorietyRank: 8,
    website: 'https://www.fpmarkets.com',
    logoUrl: null,
    foundedYear: 2005,
    headquarters: 'Sydney, Australie',
    regulation: ['ASIC (AU)', 'CySEC (CY)', 'FSA (SC)', 'FSCA (ZA)', 'CMA (KE)'],
    minDepositUsd: 100,
    spreadFromPips: 0,
    commissionPerLotUsd: 6,
    maxLeverage: "1:500 (int'l) ; 1:30 UE/AU",
    platforms: ['MT4', 'MT5', 'cTrader', 'TradingView', 'autre'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD + Iress)', 'ETF (CFD)', 'crypto (CFD)'],
    trustpilotScore: 4.8,
    trustpilotReviews: 10108,
    notes: 'Raw : 3 $/côté ; dépôt min 100 AUD ou équivalent (~65–70 USD). Plateforme Iress normalisée en « autre ».',
    sources: [
      'https://www.fpmarkets.com',
      'https://www.forexbrokers.com/reviews/fp-markets',
      'https://www.trustpilot.com/review/fpmarkets.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'FxPro',
    slug: 'fxpro',
    notorietyRank: 9,
    website: 'https://www.fxpro.com',
    logoUrl: null,
    foundedYear: 2006,
    headquarters: 'Londres, Royaume-Uni',
    regulation: ['FCA (UK)', 'CySEC (CY)', 'FSCA (ZA)', 'SCB (BS)', 'FSA (SC)'],
    minDepositUsd: 100,
    spreadFromPips: 0,
    commissionPerLotUsd: 7,
    maxLeverage: '1:500 (jusqu\'à 1:2000 selon zone) ; 1:30 UK/UE',
    platforms: ['MT4', 'MT5', 'cTrader', 'autre'],
    instruments: ['forex', 'indices', 'actions (CFD)', 'métaux', 'énergies', 'crypto (CFD)', 'futures (CFD)'],
    trustpilotScore: 3,
    trustpilotReviews: 769,
    notes: 'Raw+ : 3,50 $/côté ; Standard dès ~1,2 pip sans commission. Plateforme maison FxPro Edge normalisée en « autre ».',
    sources: [
      'https://www.fxpro.com',
      'https://www.forexbrokers.com/reviews/fxpro',
      'https://www.trustpilot.com/review/www.fxpro.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Axi',
    slug: 'axi',
    notorietyRank: 10,
    website: 'https://www.axi.com',
    logoUrl: null,
    foundedYear: 2007,
    headquarters: 'Sydney, Australie',
    regulation: ['ASIC (AU)', 'FCA (UK)', 'CySEC (CY)', 'DFSA (AE)', 'FMA (NZ)'],
    minDepositUsd: 0,
    spreadFromPips: 0,
    commissionPerLotUsd: 4.5,
    maxLeverage: "1:500 (int'l) ; 1:30 UE/UK/AU",
    platforms: ['MT4', 'MT5', 'autre'],
    instruments: ['forex', 'indices', 'métaux', 'énergies', 'matières premières', 'crypto (CFD)'],
    trustpilotScore: 4.4,
    trustpilotReviews: 3800,
    notes: 'Pro : 4,50 $ AR ; Elite : 3,50 $ AR (solde min 25 000 $) — confirmé site officiel. Entité offshore SVG non régulée. App maison Axi Mobile normalisée en « autre ».',
    sources: [
      'https://www.axi.com/int/trading-accounts',
      'https://www.compareforexbrokers.com/reviews/axi-review/',
      'https://www.trustpilot.com/review/axi.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Eightcap',
    slug: 'eightcap',
    notorietyRank: 11,
    website: 'https://www.eightcap.com',
    logoUrl: null,
    foundedYear: 2009,
    headquarters: 'Melbourne, Australie',
    regulation: ['ASIC (AU)', 'FCA (UK)', 'CySEC (CY)', 'SCB (BS)'],
    minDepositUsd: 100,
    spreadFromPips: 0,
    commissionPerLotUsd: 7,
    maxLeverage: "1:500 (int'l) ; 1:30 UE/UK/AU",
    platforms: ['MT4', 'MT5', 'TradingView', 'TradeLocker'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'crypto (CFD)'],
    trustpilotScore: 4,
    trustpilotReviews: 3429,
    notes: 'Raw : 3,50 $/côté ; Standard dès ~1,0 pip sans commission',
    sources: [
      'https://www.eightcap.com',
      'https://www.forexbrokers.com/reviews/eightcap',
      'https://www.trustpilot.com/review/eightcap.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'BlackBull Markets',
    slug: 'blackbull-markets',
    notorietyRank: 12,
    website: 'https://blackbull.com',
    logoUrl: null,
    foundedYear: 2014,
    headquarters: 'Auckland, Nouvelle-Zélande',
    regulation: ['FMA (NZ)', 'FSA (SC)'],
    minDepositUsd: 0,
    spreadFromPips: 0.1,
    commissionPerLotUsd: 6,
    maxLeverage: '1:500',
    platforms: ['MT4', 'MT5', 'cTrader', 'TradingView', 'autre'],
    instruments: ['forex', 'indices', 'matières premières', 'actions', 'crypto (CFD)'],
    trustpilotScore: 4.8,
    trustpilotReviews: 2681,
    notes: 'ECN Prime : 6 $ AR dès 0,1 pip ; ECN Standard dès 0,8 pip sans commission ; Institutional : dépôt 20 000 $. Copieur maison BlackBull CopyTrader normalisé en « autre ».',
    sources: [
      'https://blackbull.com',
      'https://www.forexbrokers.com/reviews/blackbull-markets',
      'https://www.trustpilot.com/review/blackbull.com',
    ],
    lastVerified: '2026-07-24',
  },
  {
    name: 'Tickmill',
    slug: 'tickmill',
    notorietyRank: 13,
    website: 'https://www.tickmill.com',
    logoUrl: null,
    foundedYear: 2014,
    headquarters: 'Londres, Royaume-Uni',
    regulation: ['FCA (UK)', 'CySEC (CY)', 'FSA (SC)', 'FSCA (ZA)', 'DFSA (AE)'],
    minDepositUsd: 100,
    spreadFromPips: 0,
    commissionPerLotUsd: 6,
    maxLeverage: '1:1000 (entité Seychelles) / 1:30 retail FCA-CySEC',
    platforms: ['MT4', 'MT5', 'autre', 'TradingView'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'obligations', 'crypto (CFD)', 'futures (CFD)'],
    trustpilotScore: null,
    trustpilotReviews: 1136,
    notes: "Opérationnel (juil. 2026). Compte Raw : spread dès 0.0 + 6 USD aller-retour ; compte Classic dès 1.6 pip sans commission. ATTENTION : note Trustpilot actuellement suspendue pour violation des CGU (faux avis supprimés) — 1 136 avis, 25% à 1 étoile. Fondé aux Seychelles en 2014, direction opérationnelle à Londres. Terminal maison Tickmill Trader normalisé « autre ».",
    sources: [
      'https://www.tickmill.com/about/licences-and-regulation',
      'https://www.daytrading.com/tickmill',
      'https://www.forexbrokers.com/reviews/tickmill',
      'https://fr.trustpilot.com/review/www.tickmill.com',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Admirals (Admiral Markets)',
    slug: 'admirals',
    notorietyRank: 14,
    website: 'https://admiralmarkets.com',
    logoUrl: null,
    foundedYear: 2001,
    headquarters: 'Tallinn, Estonie',
    regulation: ['FCA (UK)', 'CySEC (CY)', 'JSC (JO)', 'FSCA (ZA)', 'CMA (KE)'],
    minDepositUsd: 25,
    spreadFromPips: 0,
    commissionPerLotUsd: null,
    maxLeverage: "1:30 retail (UE/UK) / jusqu'à 1:500 (entités hors UE)",
    platforms: ['MT4', 'MT5', 'autre'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'ETF (CFD)', 'obligations', 'crypto (CFD)'],
    trustpilotScore: 4.1,
    trustpilotReviews: 2163,
    notes: "Opérationnel (juil. 2026). A quitté le marché australien début 2025 (entité ASIC vendue et rebrandée PU Prime). Dépôt min 25 USD (Trade.MT5), ~100 USD pour clients UE selon entité. Commission Zero.MT5 variable (~1,8-3 USD/côté selon instrument) — non confirmée, laissée à null. Plateforme maison Admirals Platform normalisée « autre ».",
    sources: [
      'https://iamforextrader.com/en/broker/admirals/',
      'https://safeforexbrokers.com/reviews/admiral-markets-review/',
      'https://fr.trustpilot.com/review/admiralmarkets.com',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'ThinkMarkets',
    slug: 'thinkmarkets',
    notorietyRank: 15,
    website: 'https://www.thinkmarkets.com',
    logoUrl: null,
    foundedYear: 2010,
    headquarters: 'Melbourne, Australie (double siège avec Londres)',
    regulation: ['FCA (UK)', 'ASIC (AU)', 'CySEC (CY)', 'JFSA (JP)', 'FSCA (ZA)', 'FSA (SC)'],
    minDepositUsd: 0,
    spreadFromPips: 0,
    commissionPerLotUsd: null,
    maxLeverage: "1:30 retail (FCA/ASIC) / jusqu'à 1:500 selon entité",
    platforms: ['autre', 'MT4', 'MT5', 'TradingView'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'crypto (CFD)'],
    trustpilotScore: null,
    trustpilotReviews: 529,
    notes: "Opérationnel (juil. 2026). Compte Standard sans dépôt minimum ; ThinkZero (spread brut) : 500 USD min, commission par lot non confirmée (laissée à null). ATTENTION : note Trustpilot actuellement suspendue pour violation des CGU — 529 avis. Plaintes récurrentes sur délais de retrait dans les avis. Plateforme maison ThinkTrader normalisée « autre ».",
    sources: [
      'https://fxscouts.com/broker/thinkmarkets/',
      'https://tradersunion.com/brokers/forex/view/thinkmarkets/',
      'https://fr.trustpilot.com/review/www.thinkmarkets.com',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'CMC Markets',
    slug: 'cmc-markets',
    notorietyRank: 16,
    website: 'https://www.cmcmarkets.com',
    logoUrl: null,
    foundedYear: 1989,
    headquarters: 'Londres, Royaume-Uni',
    regulation: ['FCA (UK)', 'ASIC (AU)', 'BaFin (DE)', 'MAS (SG)', 'FMA (NZ)', 'CIRO (CA)'],
    minDepositUsd: 0,
    spreadFromPips: 0,
    commissionPerLotUsd: 5,
    maxLeverage: '1:30 retail (FCA/ASIC)',
    platforms: ['autre', 'MT4'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'ETF (CFD)', 'obligations', 'crypto (CFD)'],
    trustpilotScore: 4.2,
    trustpilotReviews: 3310,
    notes: "Opérationnel (juil. 2026). Coté au LSE depuis 2016. Compte sans commission : EUR/USD dès 0,7 pip ; compte FX Active : spread 0.0 + 5 USD aller-retour (valeur retenue, convention spread-brut + commission). ~12 000 instruments. Spread betting disponible au Royaume-Uni. Plateforme maison CMC Next Generation normalisée « autre ».",
    sources: [
      'https://www.cmcmarkets.com/en/forex/forex-active-trader',
      'https://fxscouts.com/broker/cmcmarkets/',
      'https://www.compareforexbrokers.com/reviews/cmc-markets-review/',
      'https://fr.trustpilot.com/review/www.cmcmarkets.com',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'ActivTrades',
    slug: 'activtrades',
    notorietyRank: 17,
    website: 'https://www.activtrades.com',
    logoUrl: null,
    foundedYear: 2001,
    headquarters: 'Londres, Royaume-Uni',
    regulation: ['FCA (UK)', 'SCB (BS)', 'CSSF (LU)', 'CMVM (PT)', 'CVM (BR)'],
    minDepositUsd: 0,
    spreadFromPips: 0.5,
    commissionPerLotUsd: 0,
    maxLeverage: "1:30 retail (FCA) / jusqu'à 1:400 (SCB Bahamas)",
    platforms: ['autre', 'MT4', 'MT5', 'TradingView'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'ETF (CFD)', 'obligations', 'crypto (CFD)'],
    trustpilotScore: 4.5,
    trustpilotReviews: 1461,
    notes: "Opérationnel (juil. 2026). Modèle sans commission (spread only). Clients FCA protégés par le FSCS jusqu'à 85 000 GBP + assurance complémentaire. Note Trustpilot 4,5/5 citée par l'entreprise sur sa page (1 461 avis). Plateforme maison ActivTrader normalisée « autre ».",
    sources: [
      'https://tradersunion.com/brokers/forex/view/activtrades/',
      'https://www.tradingpedia.com/forex-brokers/activtrades/',
      'https://fr.trustpilot.com/review/activtrades.com',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Fusion Markets',
    slug: 'fusion-markets',
    notorietyRank: 18,
    website: 'https://fusionmarkets.com',
    logoUrl: null,
    foundedYear: 2017,
    headquarters: 'Melbourne, Australie',
    regulation: ['ASIC (AU)', 'VFSC (VU)', 'FSA (SC)'],
    minDepositUsd: 0,
    spreadFromPips: 0,
    commissionPerLotUsd: 4.5,
    maxLeverage: "1:30 retail ASIC / jusqu'à 1:500 (entités offshore)",
    platforms: ['MT4', 'MT5', 'cTrader', 'TradingView', 'autre'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'crypto (CFD)'],
    trustpilotScore: 4.8,
    trustpilotReviews: 8374,
    notes: "Opérationnel (juil. 2026). Une des commissions les plus basses du marché : 2,25 USD/côté (4,50 USD aller-retour) sur compte Zero, spread dès 0.0. Compte Classic sans commission. Excellente réputation Trustpilot (4,8/5, 8 374 avis). Copieur DupliTrade normalisé « autre ».",
    sources: [
      'https://www.daytrading.com/fusion-markets',
      'https://www.compareforexbrokers.com/reviews/fusion-markets/',
      'https://uk.trustpilot.com/review/fusionmarkets.com',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Global Prime',
    slug: 'global-prime',
    notorietyRank: 19,
    website: 'https://www.globalprime.com',
    logoUrl: null,
    foundedYear: 2010,
    headquarters: 'Melbourne (South Yarra), Australie',
    regulation: ['ASIC (AU)', 'VFSC (VU)'],
    minDepositUsd: 0,
    spreadFromPips: 0,
    commissionPerLotUsd: 7,
    maxLeverage: '1:30 retail ASIC / 1:500 (VFSC)',
    platforms: ['MT4', 'MT5'],
    instruments: ['forex', 'indices', 'matières premières', 'crypto (CFD)'],
    trustpilotScore: 4.6,
    trustpilotReviews: 505,
    notes: "Opérationnel (juil. 2026), inscriptions actives. Opère sous Gleneagle Securities Pty Ltd. Compte Raw : spread 0.0 + 3,5 USD/côté (7 USD aller-retour) ; compte Standard dès 0,9 pip sans commission. Connu pour sa transparence d'exécution (trade receipts). N'accepte pas les clients US, Japon, NZ.",
    sources: [
      'https://www.globalprime.com/',
      'https://www.daytrading.com/global-prime',
      'https://uk.trustpilot.com/review/globalprime.com',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Hantec Markets',
    slug: 'hantec-markets',
    notorietyRank: 20,
    website: 'https://hmarkets.com',
    logoUrl: null,
    foundedYear: 1990,
    headquarters: 'Londres, Royaume-Uni',
    regulation: ['FCA (UK)', 'FSC (MU)', 'ASIC (AU)', 'JSC (JO)'],
    minDepositUsd: 10,
    spreadFromPips: null,
    commissionPerLotUsd: null,
    maxLeverage: "1:30 (FCA) / jusqu'à 1:500 (FSC Maurice)",
    platforms: ['MT4', 'MT5'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'crypto (CFD)'],
    trustpilotScore: 5,
    trustpilotReviews: 4768,
    notes: "Opérationnel (juil. 2026). Groupe Hantec fondé à Hong Kong en 1990 ; entité UK depuis 2009. Les clients retail sont désormais principalement onboardés via l'entité Maurice (protection réduite vs FCA). Note Trustpilot affichée 5/5 (4 768 avis) — volume d'avis collectés activement, à interpréter avec recul. Propose aussi la prop firm Hantec Trader. Spreads/commissions non confirmés avec certitude (null).",
    sources: [
      'https://www.fxempire.com/brokers/hantec',
      'https://brokerchooser.com/broker-reviews/hantec-markets-review',
      'https://www.trustpilot.com/review/hmarkets.com',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'Moneta Markets',
    slug: 'moneta-markets',
    notorietyRank: 21,
    website: 'https://www.monetamarkets.com',
    logoUrl: null,
    foundedYear: 2019,
    headquarters: null,
    regulation: ['FSCA (ZA)', 'FSA (SC)', 'FSC (MU)', 'FCA (UK)'],
    minDepositUsd: 50,
    spreadFromPips: null,
    commissionPerLotUsd: null,
    maxLeverage: "jusqu'à 1:1000 (entité offshore)",
    platforms: ['MT4', 'MT5', 'autre'],
    instruments: ['forex', 'indices', 'matières premières', 'actions (CFD)', 'ETF (CFD)', 'obligations', 'crypto (CFD)'],
    trustpilotScore: 3.9,
    trustpilotReviews: 543,
    notes: "Opérationnel (juil. 2026). PRUDENCE : la majorité des clients sont onboardés via les entités offshore (Sainte-Lucie/Seychelles, protection limitée ; l'enregistrement IBC Sainte-Lucie n'est pas un régulateur). Licence FCA obtenue en août 2025 via rachat de VIBHS Financial — ne couvre pas les clients hors UK. Compte Direct STP dès 1,2 pip sans commission ; compte ECN brut existant mais tarifs non confirmés (null). Sponsor régional de l'Atlético de Madrid. Année de création incertaine (2019 le plus cité). Plateformes maison ProTrader/AppTrader normalisées « autre ».",
    sources: [
      'https://www.daytrading.com/moneta-markets',
      'https://www.financemagnates.com/forex/moneta-markets-gains-fca-approval-following-atltico-de-madrid-deal/',
      'https://fr.trustpilot.com/review/monetamarkets.com',
    ],
    lastVerified: '2026-07-26',
  },
  {
    name: 'RoboForex',
    slug: 'roboforex',
    notorietyRank: 22,
    website: 'https://roboforex.com',
    logoUrl: null,
    foundedYear: 2009,
    headquarters: 'Belize City, Belize',
    regulation: ['FSC (BZ)'],
    minDepositUsd: 10,
    spreadFromPips: 0,
    commissionPerLotUsd: null,
    maxLeverage: '1:2000',
    platforms: ['MT4', 'MT5', 'autre'],
    instruments: ['forex', 'actions (CFD)', 'indices', 'matières premières', 'ETF (CFD)', 'crypto (CFD)'],
    trustpilotScore: 2.3,
    trustpilotReviews: 754,
    notes: "Opérationnel (juil. 2026) mais PRUDENCE MARQUEE : régulation unique offshore tier-3 (FSC Belize, licence 9759600), aucune protection tier-1. TrustScore Trustpilot faible (2,3/5, 49% d'avis 1 étoile, plaintes récurrentes de retraits et de slippage). Contrepoids : membre catégorie A de la Financial Commission (fonds de compensation 20 000 EUR/dossier) + assurance responsabilité civile 2,5 M EUR. L'ex-branche européenne opère séparément sous la marque RoboMarkets (CySEC). Commission ECN non confirmée (null). Plateforme maison R StocksTrader normalisée « autre ».",
    sources: [
      'https://roboforex.com/about/company/regulation/',
      'https://tradersunion.com/brokers/forex/view/roboforex/',
      'https://fr.trustpilot.com/review/roboforex.com',
    ],
    lastVerified: '2026-07-26',
  },
];
